import { useEffect, useState } from 'react';
import type { WheelSeatView } from '@goodtime/shared';
import type { ReceivedReaction } from '../../hooks/useWheelRoomStream';
import { submitWheelCommand } from '../../api/wheelCommand';
import { SeatAvatar, resolveSeatName } from './SeatAvatar';
import { usePlayerName } from '../../avatar/usePlayerName';
import { Icon } from '../Icon';
import { GESTURE_BY_ID, RECIPROCATE_GESTURE_ID } from './gestures';
import { isReplyToOurGesture } from './gestureMemory';

/**
 * Directed social-gesture toast (2026-06-08 producer pivot — the
 * Peloton "someone sent you a high five" beat). When another seat sends
 * the local player a one-to-one gesture (a REACTION command carrying a
 * {@code targetSeatId} that matches our seat), this slides a toast in at
 * the TOP of the screen — above the avatar rail — showing the sender's
 * avatar, "{name} high-fived you", and a "High five back" button that
 * reciprocates. Auto-dismisses after {@link TOAST_LIFETIME_MS}.
 *
 * <p>When the incoming high-five is itself a reply to one WE just sent
 * that seat (tracked locally in {@link isReplyToOurGesture}), the copy
 * reads "{name} high-fived you back" and the button is dropped — you
 * don't reciprocate a reciprocation. The button only shows for gestures
 * someone else initiated at us.
 *
 * <p>It's an overlay (fixed, high z-index, pointer-events only on the
 * button), so it never reflows the avatars or the board. Untargeted
 * (to-the-table) gestures are NOT shown here — they float over the
 * sender's avatar via {@link ReactionFloat}. Only the single newest
 * directed-at-me gesture is shown at a time.
 *
 * <p>Renders null for audience viewers (no {@code selfSeatId}) and when
 * nothing is currently directed at the local seat.
 */
const TOAST_LIFETIME_MS = 4200;

export interface GestureToastProps {
  /** Live reaction ring buffer from {@code useWheelRoomStream}. */
  reactions: readonly ReceivedReaction[];
  /** The local player's seat id. Only gestures whose {@code targetSeatId}
   *  equals this surface as a toast. null = audience → no toasts. */
  selfSeatId: number | null;
  /** Current seats — used to resolve the sender's avatar + name. */
  seats: readonly WheelSeatView[];
  roomId: string | null;
  playerId: string | null;
}

export function GestureToast({
  reactions,
  selfSeatId,
  seats,
  roomId,
  playerId,
}: GestureToastProps) {
  const [localName] = usePlayerName();
  // clientId of the toast the player has dismissed (timer) / reciprocated.
  const [dismissedId, setDismissedId] = useState<number | null>(null);
  const [sentBackId, setSentBackId] = useState<number | null>(null);

  // Newest gesture directed AT the local seat that's still fresh.
  let active: ReceivedReaction | null = null;
  if (selfSeatId !== null) {
    const now = Date.now();
    for (const r of reactions) {
      if (r.targetSeatId !== selfSeatId) continue; // directed at me only
      if (r.seatId === selfSeatId) continue; // not my own
      if (now - r.emittedAtMs > TOAST_LIFETIME_MS) continue; // fresh
      if (!GESTURE_BY_ID[r.reactionId]) continue; // known gesture
      if (!active || r.emittedAtMs > active.emittedAtMs) active = r;
    }
  }

  // Auto-dismiss the active toast when its lifetime elapses.
  const activeId = active?.clientId ?? null;
  const activeAt = active?.emittedAtMs ?? 0;
  useEffect(() => {
    if (activeId === null) return;
    const remaining = TOAST_LIFETIME_MS - (Date.now() - activeAt);
    const handle = window.setTimeout(
      () => setDismissedId(activeId),
      Math.max(0, remaining),
    );
    return () => window.clearTimeout(handle);
  }, [activeId, activeAt]);

  if (!active || active.clientId === dismissedId) return null;

  const gesture = GESTURE_BY_ID[active.reactionId]!;
  const reciprocal = GESTURE_BY_ID[RECIPROCATE_GESTURE_ID];
  const sender = seats.find((s) => s.seatId === active.seatId) ?? null;
  const senderName = sender ? resolveSeatName(sender, localName) : 'Someone';
  // Only a high-five is reciprocable. Good-luck / cheers are one-way
  // (producer 2026-06-08: "when someone raises a glass to you or wished
  // you luck, you shouldn't have the option to high five back"), so they
  // never show the button.
  const isReciprocable = active.reactionId === RECIPROCATE_GESTURE_ID;
  // Is this high-five a reply to one WE sent this seat? Then it reads as
  // "...back" with NO reciprocate button — you don't high-five back a
  // high-five-back (that's the loop the producer wants gone). The button
  // only appears when someone ELSE initiates a high-five at us.
  const isBack =
    isReciprocable && isReplyToOurGesture(active.seatId, active.reactionId);
  const sentBack = sentBackId === active.clientId;
  const canSendBack = roomId !== null && playerId !== null && !sentBack;

  const sendBack = (): void => {
    if (!canSendBack || active === null) return;
    setSentBackId(active.clientId);
    submitWheelCommand(roomId, 'REACTION', playerId, {
      reactionId: RECIPROCATE_GESTURE_ID,
      targetSeatId: active.seatId,
      // This is a reply — don't let the server queue another bot reply
      // back (otherwise the high-five ping-pongs forever).
      reciprocal: true,
    }).catch((e) => {
      if (import.meta.env.DEV) console.warn('[GestureToast] reciprocate failed', e);
    });
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 60,
        pointerEvents: 'none',
        fontFamily: '"Fredoka", -apple-system, system-ui, sans-serif',
      }}
    >
      <div
        key={active.clientId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          width: 'max-content',
          maxWidth: 'min(96vw, 460px)',
          padding: '8px 10px 8px 8px',
          borderRadius: 999,
          background: 'linear-gradient(180deg, rgba(24,40,82,0.96), rgba(12,22,52,0.96))',
          border: '2px solid rgba(168, 224, 255, 0.9)',
          boxShadow: '0 6px 22px rgba(0,0,0,0.5), 0 0 18px rgba(120,200,255,0.4)',
          pointerEvents: 'auto',
          animation: 'gtGestureToastIn 240ms cubic-bezier(0.18,0.7,0.32,1) both',
        }}
      >
        {/* Sender avatar with the gesture sprite badged on its corner. */}
        <div style={{ position: 'relative', flexShrink: 0, width: 40, height: 40 }}>
          {sender ? (
            <SeatAvatar seat={sender} size={40} />
          ) : (
            <Icon name="profile" size={40} alt="" />
          )}
          <img
            src={gesture.src}
            alt=""
            width={24}
            height={24}
            draggable={false}
            style={{
              position: 'absolute',
              right: -6,
              bottom: -6,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.55))',
            }}
          />
        </div>

        <span
          style={{
            color: '#EAF4FF',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.2,
            textShadow: '0 1px 1px rgba(0,0,0,0.6)',
            whiteSpace: 'normal',
          }}
        >
          <strong style={{ fontWeight: 900 }}>{senderName}</strong>{' '}
          {isBack ? `${gesture.toast} back` : gesture.toast}
        </span>

        {isReciprocable && !isBack ? (
        <button
          type="button"
          onClick={sendBack}
          disabled={!canSendBack}
          aria-label={sentBack ? 'High five sent' : 'High five back'}
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            borderRadius: 999,
            border: 'none',
            cursor: canSendBack ? 'pointer' : 'default',
            background: sentBack
              ? 'rgba(124, 255, 171, 0.22)'
              : 'linear-gradient(180deg, #FFE07F, #E0A21C)',
            color: sentBack ? '#7CFFAB' : '#3a2600',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: 0.3,
            whiteSpace: 'nowrap',
          }}
        >
          {sentBack ? (
            <>
              <Icon name="check" size={14} alt="" /> Sent
            </>
          ) : (
            <>
              <img
                src={reciprocal?.src}
                alt=""
                width={16}
                height={16}
                draggable={false}
                style={{ display: 'block' }}
              />
              High five back
            </>
          )}
        </button>
        ) : null}
      </div>

      <style>{`
        @keyframes gtGestureToastIn {
          0%   { opacity: 0; transform: translateY(-14px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
